import Head from "next/head";
import { Product, Variant } from "@/types/types";
import { getStructuredData } from "@/utils/seo/getStructuredData";

type Props = {
  /**
   * The product.
   */
  product: Product;
  /**
   * The current variant of the product.
   */
  variant: Variant;
  /**
   * Whether the variant is selected.
   */
  variantIsSelected: boolean;
  /**
   * The canonical URL to display.
   */
  canonicalURL: string;
  /**
   * The meta description to display.
   */
  metaDescription: string;
  /**
   * Browser title
   */
  title: string;
};

/**
 * The Next.js Meta component that defines the meta information for the product page.
 */
export const MetaUI = ({
  product,
  variant,
  variantIsSelected,
  canonicalURL,
  metaDescription,
  title,
}: Props) => {
  const fullCanonical = `https://www.johnlewis.com${canonicalURL}`;

  return (
    <Head>
      <title>{title}</title>
      <meta name="description" content={metaDescription} />
      <link rel="canonical" href={fullCanonical} />
      <link rel="alternate" href={fullCanonical} hrefLang="x-default" />
      <meta
        name="robots"
        content={product.seo.index ? "index,follow" : "noindex"}
      />
      <meta name="format-detection" content="telephone=no" />
      <meta name="og:locale" content="en_GB" />
      <meta name="og:type" content="product" />
      <meta name="og:image:type" content="image/jpeg" />
      <meta
        name="og:image"
        content={`https:${variant.images.primary}?$rsp-pdp-port-320$`}
      />
      <meta name="og:site-name" content="John Lewis & Partners" />
      <meta name="og:title" content={title} />
      <meta name="og:description" content={metaDescription} />
      <meta name="og:url" content={fullCanonical} />
      <link rel="icon" href="/favicon.ico" sizes="any" />
      <link
        rel="icon"
        href="/static/core/images/favicon.svg"
        type="image/svg+xml"
      />
      <link
        rel="apple-touch-icon"
        href="/static/core/images/apple-touch-icon.png"
      />
      <link rel="manifest" href="/static/core/assets/manifest.json" />
      <meta name="theme-color" content="#ffffff" />
      <meta
        name="apple-itunes-app"
        content="app-id=486502369"
        // eslint-disable-next-line react/no-unknown-property
        app-argument="m.johnlewis.com"
      />
      <script
        data-test-id="structured-data"
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(
            getStructuredData(product, variant, variantIsSelected),
          ),
        }}
      />
    </Head>
  );
};
